import { Thing } from 'schema-dts';

import Seo from '~/src/common/components/Seo';
import { IMAGE_TRANSFORM_PRODUCT } from '~/src/common/components/Seo/constants';
import { getSeoImage } from '~/src/common/components/Seo/utils';
import { sanitize } from '~/src/common/utils/text';
import constants from '~/src/constants';
import { GetRecipeByIdOrSlug200 } from '~/src/queries/api-ecom/generated/api-ecom.schemas';

interface Props {
  recipe: GetRecipeByIdOrSlug200;
}

export const SeoRecipe = ({ recipe }: Props) => {
  const {
    name,
    slugs,
    images,
    description,
    breadcrumbs,
    cookingTime,
    preparationTime,
    type,
    cookTypes,
    servings,
    ingredients,
    steps,
    seo,
  } = recipe;

  // Données de base
  const image = getSeoImage(images?.[0]?.url, IMAGE_TRANSFORM_PRODUCT);
  const { canonical, title, description: metaDesc } = seo;
  const metaTitle = title ?? name;
  const metaDescription = metaDesc ?? description;
  const metaCanonical = canonical ?? `${constants.SITE_URL}/recette/${slugs[0]}`;

  // JSON-LD
  const schemas: Thing[] = [
    // https://schema.org/Recipe
    {
      '@type': 'Recipe',
      'name': name,
      'cookTime': `PT${cookingTime}M`,
      'prepTime': `PT${preparationTime}M`,
      'totalTime': `PT${cookingTime + preparationTime}M`,
      'description': sanitize(description),
      'recipeCategory': type,
      'recipeCuisine': cookTypes.join(', '),
      'recipeIngredient': ingredients.map(i => `${i.quantity} ${i.name}`),
      'recipeInstructions': steps.map(s => ({
        '@type': 'HowToStep',
        'text': sanitize(s.description),
      })),
      'recipeYield': servings.toString(),
      'author': [
        { '@type': 'Organization', 'name': 'Mon-marché.fr', 'url': 'https://www.mon-marche.fr' },
      ],
      ...(image ? { image } : null),
    },
    // https://schema.org/BreadcrumbList
    {
      '@type': 'BreadcrumbList',
      'name': 'Categories breadcrumbs',
      'itemListElement': [
        ...(breadcrumbs ?? [])
          .filter(c => c.enabled)
          .map(c => ({
            name: c.name,
            item: `${constants.SITE_URL}/categorie/${c.slug}`,
          })),
        { name },
      ].map((item, index) => ({
        '@type': 'ListItem' as const,
        'position': index + 1,
        ...item,
      })),
    },
  ];
  return (
    <Seo
      title={metaTitle}
      image={image}
      metaDescr={metaDescription}
      canonicalUrl={metaCanonical}
      schemas={schemas}
      noSuffix={!!title}
    />
  );
};
